import { useEffect, useState } from 'react'
import {
  Badge,
  Banner,
  BlockStack,
  DropZone,
  InlineStack,
  Modal,
  Spinner,
  Text,
} from '@shopify/polaris'
import { AttachmentIcon } from '@shopify/polaris-icons'
import * as XLSX from 'xlsx'
import { post } from '../../../../sdk/client'
import { getShop } from '../../../../sdk/shop'
import { healthApi, type AffectedRow, type HealthReport, type IssueDetail, type ValidatorSeverity } from '../../../../sdk/health.api'

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'view' | 'upload' | 'report' | 'applying' | 'done'

type FixRow = { rowId: string; fields: Record<string, string> }

type ValidationRow = {
  rowId:    string
  newValue: Record<string, string>
  status:   'accepted' | 'rejected'
  reason?:  string
}

type FixResponse = {
  applied: number
  results: { rowId: string; status: string; reason?: string }[]
  report:  HealthReport | null
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SEVERITY_TONE: Record<ValidatorSeverity, 'critical' | 'warning' | 'info'> = {
  critical: 'critical', warning: 'warning', info: 'info',
}
const SEVERITY_LABEL: Record<ValidatorSeverity, string> = {
  critical: 'Critical', warning: 'Warning', info: 'Info',
}

// ── Props ─────────────────────────────────────────────────────────────────────

type Props = {
  validatorId:     string | null
  onClose:         () => void
  onReportUpdated: (report: HealthReport) => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FixPanel({ validatorId, onClose, onReportUpdated }: Props) {
  const shop = getShop() ?? ''

  const [detail,      setDetail]      = useState<IssueDetail | null>(null)
  const [loading,     setLoading]     = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [phase,       setPhase]       = useState<Phase>('view')
  const [uploadFile,  setUploadFile]  = useState<File | null>(null)
  const [validation,  setValidation]  = useState<ValidationRow[]>([])
  const [fixRows,     setFixRows]     = useState<FixRow[]>([])
  const [applying,    setApplying]    = useState(false)
  const [applyCount,  setApplyCount]  = useState(0)

  // Load issue detail whenever validatorId changes
  useEffect(() => {
    if (!validatorId || !shop) return
    setDetail(null)
    setError(null)
    setPhase('view')
    setUploadFile(null)
    setValidation([])
    setFixRows([])
    setApplyCount(0)
    setLoading(true)
    healthApi.getIssues(shop, validatorId)
      .then(setDetail)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load issues'))
      .finally(() => setLoading(false))
  }, [validatorId, shop])

  // ── Template download ───────────────────────────────────────────────────────

  function downloadTemplate() {
    if (!detail || detail.result.affectedRows.length === 0) return
    const fixable  = detail.result.affectedRows[0]?.fixable ?? []
    const idLabel  = detail.validator.id.startsWith('inventory') ? 'sku' : 'order_name'
    const headers  = [idLabel, ...fixable]
    const dataRows = detail.result.affectedRows.map(row => [
      row.rowId,
      ...fixable.map(f => row.fields[f] ?? ''),
    ])
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Fixes')
    XLSX.writeFile(wb, `fix_${detail.validator.id.replace('.', '_')}.xlsx`)
  }

  // ── File drop & parse ───────────────────────────────────────────────────────

  function handleDropAccepted(files: File[]) {
    const file = files[0]
    if (!file || !detail) return
    setUploadFile(file)
    setError(null)

    const fixable     = detail.result.affectedRows[0]?.fixable ?? []
    const idLabel     = detail.validator.id.startsWith('inventory') ? 'sku' : 'order_name'
    const affectedIds = new Set(detail.result.affectedRows.map(r => r.rowId))

    file.arrayBuffer().then(buf => {
      const wb  = XLSX.read(buf, { type: 'array' })
      const ws  = wb.Sheets[wb.SheetNames[0] ?? '']
      if (!ws) { setError('File has no sheets'); return }
      const raw = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' })

      const validationRows: ValidationRow[] = []
      const acceptedFixRows: FixRow[]       = []

      for (const r of raw) {
        const rowId = String(r[idLabel] ?? '').trim()
        if (!rowId) {
          validationRows.push({ rowId: '(blank)', newValue: {}, status: 'rejected', reason: `${idLabel} column is empty` })
          continue
        }
        if (!affectedIds.has(rowId)) {
          validationRows.push({ rowId, newValue: {}, status: 'rejected', reason: 'Not in affected rows list' })
          continue
        }
        const newValue: Record<string, string> = {}
        for (const f of fixable) {
          const val = String(r[f] ?? '').trim()
          if (val) newValue[f] = val
        }
        if (Object.keys(newValue).length === 0) {
          validationRows.push({ rowId, newValue, status: 'rejected', reason: 'No fixable field values provided' })
          continue
        }
        validationRows.push({ rowId, newValue, status: 'accepted' })
        acceptedFixRows.push({ rowId, fields: newValue })
      }

      setValidation(validationRows)
      setFixRows(acceptedFixRows)
      setPhase('report')
    }).catch(e => setError(e instanceof Error ? e.message : 'Failed to parse file'))
  }

  // ── Apply fixes ─────────────────────────────────────────────────────────────

  async function handleApply() {
    if (!detail || fixRows.length === 0) return
    setApplying(true)
    setError(null)
    try {
      const res = await post<FixResponse>(
        `/system-health/fix?shop=${encodeURIComponent(shop)}`,
        { validatorId: detail.validator.id, rows: fixRows },
        { timeout: 60_000 },
      )
      setApplyCount(res.applied)
      if (res.report) onReportUpdated(res.report)
      setPhase('done')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to apply fixes')
    } finally {
      setApplying(false)
    }
  }

  // ── Reset & close ───────────────────────────────────────────────────────────

  function handleClose() {
    setPhase('view')
    setUploadFile(null)
    setValidation([])
    setFixRows([])
    setApplyCount(0)
    setError(null)
    onClose()
  }

  // ── Derived values ──────────────────────────────────────────────────────────

  const open     = !!validatorId
  const fixable  = detail?.result.affectedRows[0]?.fixable ?? []
  const idLabel  = (detail?.validator.id.startsWith('inventory') || detail?.validator.id === 'products.missing-cost') ? 'sku' : 'order_name'
  const accepted = validation.filter(r => r.status === 'accepted').length
  const rejected = validation.filter(r => r.status === 'rejected').length

  function modalTitle() {
    if (phase === 'report') return 'Validation Report'
    if (phase === 'done')   return 'Fixes Applied'
    return detail?.validator.title ?? 'Loading…'
  }

  const primaryAction = (() => {
    if (phase === 'view')   return { content: 'Upload Corrected File', onAction: () => setPhase('upload') }
    if (phase === 'report') return {
      content:  `Apply ${accepted} Fix${accepted !== 1 ? 'es' : ''}`,
      onAction: handleApply,
      loading:  applying,
      disabled: accepted === 0 || applying,
    }
    if (phase === 'done')   return { content: 'Close', onAction: handleClose }
    return undefined
  })()

  const secondaryActions = (() => {
    if (phase === 'view')   return [
      { content: 'Download Fix Template', onAction: downloadTemplate, disabled: !detail || detail.result.affectedRows.length === 0 },
      { content: 'Close',                 onAction: handleClose },
    ]
    if (phase === 'upload') return [{ content: 'Back', onAction: () => setPhase('view') }]
    if (phase === 'report') return [
      { content: 'Back',   onAction: () => setPhase('upload') },
      { content: 'Cancel', onAction: handleClose },
    ]
    return [{ content: 'Close', onAction: handleClose }]
  })()

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle()}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      size="large"
    >
      <Modal.Section>
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Banner tone="critical" onDismiss={() => setError(null)}><Text as="p">{error}</Text></Banner>
          </div>
        )}

        {/* ── VIEW ──────────────────────────────────────────────────────── */}
        {phase === 'view' && (
          <BlockStack gap="400">
            {loading && <InlineStack align="center"><Spinner accessibilityLabel="Loading issues" /></InlineStack>}
            {detail && (
              <>
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone={SEVERITY_TONE[detail.validator.severity]}>
                    {SEVERITY_LABEL[detail.validator.severity]}
                  </Badge>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {detail.result.affectedCount} of {detail.result.totalRows} orders affected
                  </Text>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">{detail.validator.description}</Text>
                {detail.result.affectedRows.length > 0 && (
                  <AffectedTable rows={detail.result.affectedRows} fixable={fixable} />
                )}
                {detail.result.affectedRows.length === 0 && (
                  <Text as="p" tone="subdued">No issues found.</Text>
                )}
              </>
            )}
          </BlockStack>
        )}

        {/* ── UPLOAD ────────────────────────────────────────────────────── */}
        {phase === 'upload' && (
          <BlockStack gap="400">
            <Text as="p" variant="bodySm" tone="subdued">
              Upload the corrected Excel file. The first column must be <strong>{idLabel}</strong>.
              Only entries whose {idLabel} matches an affected order will be applied.
            </Text>
            <DropZone
              accept=".xlsx,.xls"
              allowMultiple={false}
              onDropAccepted={handleDropAccepted}
              label="Corrected file"
              labelHidden
            >
              {uploadFile ? (
                <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AttachmentIcon
                    aria-hidden="true"
                    style={{ width: 20, height: 20, fill: '#5c5f62' } as React.CSSProperties}
                  />
                  <Text as="span" variant="bodySm" fontWeight="semibold">{uploadFile.name}</Text>
                </div>
              ) : (
                <DropZone.FileUpload actionTitle="Add file" actionHint="or drag and drop .xlsx" />
              )}
            </DropZone>
          </BlockStack>
        )}

        {/* ── REPORT ────────────────────────────────────────────────────── */}
        {phase === 'report' && (
          <BlockStack gap="400">
            <InlineStack gap="300">
              {accepted > 0 && (
                <Banner tone="success">
                  <Text as="p" variant="bodySm">{accepted} item{accepted !== 1 ? 's' : ''} accepted</Text>
                </Banner>
              )}
              {rejected > 0 && (
                <Banner tone="warning">
                  <Text as="p" variant="bodySm">{rejected} item{rejected !== 1 ? 's' : ''} rejected</Text>
                </Banner>
              )}
            </InlineStack>
            <ValidationTable rows={validation} fixable={fixable} />
          </BlockStack>
        )}

        {/* ── DONE ──────────────────────────────────────────────────────── */}
        {phase === 'done' && (
          <Banner tone="success">
            <Text as="p" variant="bodySm">
              {applyCount} fix{applyCount !== 1 ? 'es' : ''} applied successfully.
              The health report has been refreshed automatically.
            </Text>
          </Banner>
        )}
      </Modal.Section>
    </Modal>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

const TH: React.CSSProperties = { padding: '6px 12px', textAlign: 'left', fontWeight: 600, color: '#6d7175', whiteSpace: 'nowrap' }
const TD: React.CSSProperties = { padding: '6px 12px', borderTop: '1px solid #f1f2f3' }

function AffectedTable({ rows, fixable }: { rows: AffectedRow[]; fixable: string[] }) {
  return (
    <div style={{ overflowY: 'auto', maxHeight: 360, border: '1px solid #e1e3e5', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ position: 'sticky', top: 0, background: '#f6f6f7' }}>
          <tr>
            <th style={TH}>Order / Item</th>
            {fixable.map(f => <th key={f} style={TH}>{f.replace(/_/g, ' ')}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 200).map((row, i) => (
            <tr key={row.rowId + i}>
              <td style={TD}>{row.display}</td>
              {fixable.map(f => (
                <td key={f} style={{ ...TD, color: '#6d7175' }}>{row.fields[f] || '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ValidationTable({ rows, fixable }: { rows: ValidationRow[]; fixable: string[] }) {
  return (
    <div style={{ overflowY: 'auto', maxHeight: 360, border: '1px solid #e1e3e5', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead style={{ position: 'sticky', top: 0, background: '#f6f6f7' }}>
          <tr>
            <th style={TH}>Order / SKU</th>
            {fixable.map(f => <th key={f} style={TH}>{f.replace(/_/g, ' ')}</th>)}
            <th style={TH}>Status</th>
            <th style={TH}>Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.rowId + i}>
              <td style={TD}>{row.rowId}</td>
              {fixable.map(f => (
                <td key={f} style={{ ...TD, color: '#6d7175' }}>{row.newValue[f] || '—'}</td>
              ))}
              <td style={TD}>
                <Badge tone={row.status === 'accepted' ? 'success' : 'critical'}>
                  {row.status === 'accepted' ? 'Accepted' : 'Rejected'}
                </Badge>
              </td>
              <td style={{ ...TD, color: '#6d7175' }}>{row.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
